import { useCallback, useEffect, useState } from 'react';
import {
  describeError,
  fetchHrPeriods,
  fetchReminderCandidates,
  fetchReminderLog,
  logReminders,
  type ReminderCandidate,
  type ReminderLogRow,
} from '../api.js';
import { formatDate, formatHoursTotal } from '../format.js';
import {
  DEFAULT_BODY,
  DEFAULT_SUBJECT,
  PLACEHOLDERS,
  gmailComposeUrl,
  monthLabelUa,
  renderLetter,
} from '../reminders.js';

/**
 * Рассылка напоминаний о списании часов (решение 2026-09-23).
 *
 * Экран **не отправляет почту** — он составляет письмо и открывает Gmail
 * в новой вкладке обычной ссылкой. Отправитель — та учётка Gmail, под которой
 * человек вошёл в браузере.
 *
 * Объяснения этого на экране нет (решение заказчика 2026-09-23): тот, кто ведёт
 * рассылку, узнаёт правило один раз, а строка висела бы над таблицей всегда. Поэтому и кнопка называется «Написать», а журнал пишет
 * «письмо открыто»: подтвердить отправку нам нечем, а врать в журнале дороже,
 * чем не знать. Рассылка идёт по нажатию; график появится позже.
 */
/**
 * Редактор шаблона письма спрятан (решение заказчика 2026-09-23).
 *
 * Текст устоялся, а блок занимал место на каждом заходе — правят его раз
 * в полгода, а видят каждый день. Код оставлен целиком **намеренно**: когда
 * поводов для писем станет больше, редактор понадобится снова, и вернуть его
 * будет стоить одного `true`. Удалять то, что заказчик просил сохранить, —
 * значит переписывать это заново через месяц.
 */
const TEMPLATE_EDITOR: boolean = false;

const MONTHS = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];

/**
 * Месяц не выбирают (уточнение заказчика 2026-09-23): напоминают всегда про
 * последний табель. Селектор из шести месяцев обещал выбор, которого нет,
 * и занимал место ради него.
 *
 * Берём **последний загруженный месяц**, а не календарный текущий: файл
 * PeopleForce приходит в конце месяца, и первого числа календарный «текущий»
 * был бы пуст ровно тогда, когда напоминают за прошлый.
 *
 * С 2026-10-08 этот месяц — **источник списка, а не утверждение о периоде**.
 * Он отстаёт от календаря (восьмого октября последний табель — сентябрьский),
 * и пока он стоял в письме, получатель читал это как ошибку системы. Письмо
 * месяц больше не называет, а экран говорит прямо, по какому табелю построен
 * список: числа в таблице проверяемы, потому что рядом видно, откуда они.
 */
function monthLabel(from: string): string {
  const date = new Date(`${from}T00:00:00Z`);
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

/** «3 дня назад» вместо даты: важен возраст письма, а не его календарь. */
function ago(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'сегодня';
  if (days === 1) return 'вчера';
  return `${days} дн. назад`;
}

export function Reminders({ canEdit = false }: { canEdit?: boolean }) {
  const [period, setPeriod] = useState<{ from: string; to: string }>();
  const [people, setPeople] = useState<ReminderCandidate[]>([]);
  const [unlinked, setUnlinked] = useState<string[]>([]);
  const [log, setLog] = useState<ReminderLogRow[]>([]);
  const [query, setQuery] = useState('');
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [error, setError] = useState<string>();
  const [version, setVersion] = useState(0);

  // Последний загруженный табель и есть месяц вкладки.
  useEffect(() => {
    const controller = new AbortController();
    fetchHrPeriods(controller.signal).then(
      (periods) => {
        const latest = periods[0];
        setPeriod(latest ? { from: latest.from, to: latest.to } : undefined);
      },
      (cause: unknown) => {
        if (!controller.signal.aborted) setError(describeError(cause));
      },
    );
    return () => controller.abort();
  }, [version]);

  useEffect(() => {
    if (!period) return;
    const controller = new AbortController();
    Promise.all([
      fetchReminderCandidates(period, controller.signal),
      fetchReminderLog(period, controller.signal),
    ]).then(
      ([candidates, rows]) => {
        setPeople(candidates.people);
        setUnlinked(candidates.unlinked);
        setLog(rows);
      },
      (cause: unknown) => {
        if (!controller.signal.aborted) setError(describeError(cause));
      },
    );
    return () => controller.abort();
  }, [period, version]);

  const letterFor = useCallback(
    (person: ReminderCandidate) => ({
      subject: renderLetter(subject, letterVars(person, period?.from ?? '')),
      body: renderLetter(body, letterVars(person, period?.from ?? '')),
    }),
    [subject, body, period?.from],
  );

  /**
   * Письмо открывается **обычной ссылкой** `target="_blank"`, а не `window.open`
   * (исправление 2026-09-23).
   *
   * Первая версия звала `window.open(url, '_blank', 'noopener,noreferrer')`
   * и считала блокировкой `null` в ответе. Но с `noopener` браузер возвращает
   * `null` **всегда**: новое окно намеренно не отдаётся вызвавшему. Экран
   * ругался на блокировку, которой не было, и не писал в журнал — при том что
   * Gmail исправно открывался.
   *
   * Ссылка надёжнее и по существу: переход по ней — обычная навигация,
   * блокировщики всплывающих окон её не касаются. Нам остаётся записать,
   * что письмо было открыто.
   */
  const logOpened = useCallback(
    (person: ReminderCandidate) => {
      if (!person.email || !period) return;
      const letter = letterFor(person);

      logReminders(period, [
        {
          userKey: person.userKey,
          email: person.email,
          subject: letter.subject,
          body: letter.body,
        },
      ]).then(
        // Подтверждения на экране нет намеренно: в строке человека сразу
        // появляется «писали сегодня», и это тот же ответ, только на месте.
        () => setVersion((current) => current + 1),
        (cause: unknown) => setError(describeError(cause)),
      );
    },
    [letterFor, period],
  );

  const visible = people.filter((person) => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return [person.displayName, person.email, person.userKey].some((value) =>
      value?.toLowerCase().includes(needle),
    );
  });

  // Предпросмотр — первое письмо списка: галочки обещали групповую отправку,
  // которой нет (Gmail открывается по одному), и только занимали строку.
  /**
   * Предпросмотр — **образец письма**, а не письмо первого человека списка
   * (правка заказчика 2026-09-23). На чужой фамилии и чужих часах его читают
   * как «вот что уйдёт Альбіні», хотя каждому уйдёт своё; образец показывает
   * форму письма, а числа у всех разные.
   */
  const sample = period
    ? {
        subject: renderLetter(subject, SAMPLE_VARS(period.from)),
        body: renderLetter(body, SAMPLE_VARS(period.from)),
      }
    : undefined;

  return (
    <section className="revision">
      {!canEdit && (
        <div className="muted small">
          Рассылку ведёт тот, кто ведёт сверку. Смотреть список и журнал можно всем.
        </div>
      )}

      <div className="hr-month-bar">
        <div className="hr-month-line">
          <span className="hr-month-pick">Список</span>
          <span className="reminder-month">
            {period ? `по табелю за ${monthLabel(period.from)}` : '—'}
          </span>
          <span className="muted small">
            последний загруженный · {people.length} человек · писем по нему {log.length}
            {unlinked.length > 0 && ` · без учётки Jira ${unlinked.length}: ${unlinked.join(', ')}`}
          </span>
        </div>
      </div>

      {!period && (
        <div className="empty">
          <p>Табеля PeopleForce пока нет.</p>
          <p className="muted">
            Напоминания считаются по людям из табеля — загрузите его на вкладке «Сверка с
            PeopleForce».
          </p>
        </div>
      )}

      {error && <div className="error">{error}</div>}

      {TEMPLATE_EDITOR && (
        <details className="hr-legend-box">
          <summary>
            <span className="disclosure-arrow">▸</span>
            <span className="hr-legend-title">Текст письма</span>
            <span className="hr-legend-gist muted">
              українською, один шаблон на всех — числа подставляются каждому свои
            </span>
          </summary>
          <div className="reminder-template">
            <label>
              Тема
              <input value={subject} onChange={(event) => setSubject(event.target.value)} />
            </label>
            <label>
              Текст
              <textarea rows={10} value={body} onChange={(event) => setBody(event.target.value)} />
            </label>
            <div className="muted small">Поля: {PLACEHOLDERS.join(' · ')}</div>
          </div>
        </details>
      )}

      {sample && (
        <div className="reminder-preview">
          <div className="muted small">Образец письма — имя и числа у каждого свои</div>
          <div className="reminder-subject">{sample.subject}</div>
          <pre>{sample.body}</pre>
        </div>
      )}

      <div className="hr-search">
        <input
          className="search"
          placeholder="Поиск по имени, логину или почте"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      <table className="revision-table reminder-table">
        <thead>
          <tr>
            <th>Сотрудник</th>
            {/* Кнопка и отметка об отправке — сразу за именем: искать глазами
                действие в конце широкой строки дороже, чем прочитать имя. */}
            <th />
            <th>Почта</th>
            <th className="num">Jira, ч</th>
            <th className="num">Табель, ч</th>
            <th className="num">Не разнесено, ч</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((person) => (
            <tr key={person.userKey}>
              <td>{person.displayName}</td>
              <td className="reminder-action">
                {canEdit && person.email && (
                  <a
                    className="chip"
                    href={gmailComposeUrl(
                      person.email,
                      letterFor(person).subject,
                      letterFor(person).body,
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => logOpened(person)}
                  >
                    написать
                  </a>
                )}
                {person.remindedAt && (
                  <span
                    className="muted small"
                    title={`последнее письмо ${formatDate(person.remindedAt)}`}
                  >
                    писали {ago(person.remindedAt)}
                    {person.reminders > 1 ? ` · ${person.reminders} письма` : ''}
                  </span>
                )}
              </td>
              <td className={person.email ? 'muted small' : 'muted small reminder-noemail'}>
                {person.email ?? 'почты в учётке нет'}
              </td>
              <td className="num">{formatHoursTotal(person.jiraSeconds)}</td>
              <td className="num">
                {person.hrSeconds === null ? '—' : formatHoursTotal(person.hrSeconds)}
              </td>
              <td className="num">
                {person.remainderSeconds === null ? '—' : formatHoursTotal(person.remainderSeconds)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {log.length > 0 && (
        <details className="people-left">
          <summary>
            Журнал писем за {period ? monthLabel(period.from) : 'месяц'}: {log.length}
          </summary>
          <table className="revision-table">
            <thead>
              <tr>
                <th>Когда</th>
                <th>Кому</th>
                <th>Тема</th>
                <th>Кто</th>
              </tr>
            </thead>
            <tbody>
              {log.map((row) => (
                <tr key={row.id}>
                  <td className="muted small">{formatDate(row.createdAt)}</td>
                  <td>{row.displayName ?? row.userKey}</td>
                  <td className="muted small">{row.subject}</td>
                  <td className="muted small">{row.createdBy ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </section>
  );
}

/**
 * Числа образца — не средние и не чьи-то: круглые, чтобы их не приняли
 * за настоящие. В письме по умолчанию их больше нет (2026-10-08), но шаблон
 * правится на экране, и предпросмотр обязан показать, во что превратятся поля,
 * если их вернуть.
 */
function SAMPLE_VARS(from: string) {
  return {
    name: "Ім'я Прізвище",
    month: monthLabelUa(from),
    jiraHours: 128,
    hrHours: 160,
    remainderHours: 32,
  };
}

function letterVars(person: ReminderCandidate, from: string) {
  return {
    name: person.displayName,
    // Месяц в письме — по-украински: письмо идёт сотруднику, а экран вокруг
    // остаётся русским, и это разные адресаты.
    month: monthLabelUa(from),
    jiraHours: person.jiraSeconds / 3600,
    hrHours: person.hrSeconds === null ? null : person.hrSeconds / 3600,
    remainderHours: person.remainderSeconds === null ? null : person.remainderSeconds / 3600,
  };
}
